<script lang="ts">
  import Dropdown from "$app/components/Dropdown.svelte"
  import {
    MessageSquareText,
    CircleEllipsis,
    Info,
    Trash2
  } from "lucide-svelte"
  import ProgressPanel from "$app/Pages/Plans/Goals/ProgressPanel.svelte"
  import EditGoalStatus from "$app/Pages/Plans/Goals/EditGoalStatus.svelte"
  import TaskDetail from "../TaskDetail.svelte"
  import Modal from "$app/components/Modal.svelte"
  import { formatDate, toast, wait } from "$app/utils"
  import { needsUpdate } from "$app/utils/goals.svelte"
  import { deleteTask } from "../methods"
  import { appStore } from "$app/store.svelte"

  interface Props {
    task: TaskType
    subtask?: boolean
    onupdate?: (p: TaskType) => void
  }

  let props = $props()
  let { subtask = false, onupdate = () => {} }: Props = props
  let task = $state(props.task) as TaskType

  const last_meeting_date: Date = appStore.last_meeting_date
  const today: Date = appStore.today

  const users: UserType[] = appStore.users
  const departments: DepartmentType[] = appStore.departments
  let open_progress = $state(false)
  let openTaskDetail = $state(false)
  let openTaskDropdown = $state(false)
  let selected = $state(false)

  function getAssignee(tg: TaskType): UserType | DepartmentType {
    if (tg.assignee_type === "Department") {
      return departments.find((d) => d.id === tg.assignee_id)
    } else {
      return users.find((u) => u.id === tg.assignee_id)
    }
  }

  function getAssigneeName(tg: TaskType): string {
    const assignee: UserType | DepartmentType = getAssignee(tg)
    if (assignee) {
      return assignee.name
    } else {
      return "Unassigned"
    }
  }

  $effect(() => {
    task.needsUpdate = needsUpdate(task, last_meeting_date, today)
  })
</script>

<div
  class="border-l-4 border-task task"
  role="button"
  tabindex="0"
  ondblclick={() => (openTaskDetail = true)}
>
  <div class="border-b border-gray-200">
    <div class="border-t border-gray-200 py-3 px-4 relative">
      <div class="grid grid-cols-8">
        <div
          class="font-semibold text-sm flex items-center {subtask
            ? 'col-span-6 md:col-span-5'
            : 'col-span-9 md:col-span-3'}"
        >
          {task.name}
        </div>

        {#if !subtask}
          <div
            class="text-xs text-gray-800 mr-4 flex items-center justify-end col-span-3 md:col-span-2"
          >
            {getAssigneeName(task)}
          </div>
        {/if}

        <div class="relative flex items-center justify-center">
          <Dropdown bind:open={open_progress}>
            {#snippet button()}
              <span
                class="focus:outline-none text-blue-400"
                class:text-red-400={task.needsUpdate}
                role="button"
                tabindex="0"
                title={task.needsUpdate ? "Click to add a new Progress" : ""}
              >
                <MessageSquareText class="w-6 h-6" />
              </span>
            {/snippet}

            <div>
              <div
                class="origin-top-right absolute -left-48 mt-2 w-96 lg:w-120 bg-white focus:outline-none z-10"
              >
                <ProgressPanel
                  goal={task}
                  type="task"
                  oncreate={(e) => {
                    open_progress = false
                    toast("Progress added", "success")
                    onupdate({
                      ...task,
                      progress_date: new Date(e.created_at)
                    })
                  }}
                  onclose={() => (open_progress = false)}
                />
              </div>
            </div>
          </Dropdown>
        </div>

        <div class="flex items-center justify-center">
          <EditGoalStatus
            goalOrTask={task}
            type="Task"
            editable={true}
            small={true}
            show_border={false}
            onupdate={(data) => {
              onupdate({
                ...task,
                ...data
              })
            }}
          />
        </div>

        <div class="items-center grid grid-cols-2">
          <div class="px-2 flex items-center">
            {#if task.status !== "complete" && task.status !== "not_met"}
              <span
                class="text-xs text-gray-600 bg-indigo-50 px-1 rounded-lg py-0.5"
              >
                {formatDate(task.expected_at, "short")}
              </span>
            {/if}
          </div>
          <div class="relative flex justify-end items-center pl-2">
            <Dropdown bind:open={openTaskDropdown}>
              {#snippet button()}
                <span
                  data-name="task-context-menu"
                  class="rounded-full flex items-center justify-center focus:outline-none"
                >
                  <CircleEllipsis class="text-gray-400 w-7 h-7" />
                </span>
              {/snippet}

              <div>
                <div
                  class="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-gray-200 ring-opacity-5 focus:outline-none z-10"
                  role="menu"
                >
                  <div class="py-1" role="none">
                    <button
                      onclick={() => {
                        openTaskDropdown = false
                        openTaskDetail = true
                      }}
                      class="btn-dropdown"
                      role="menuitem"
                    >
                      <Info class="text-gray-400 w-4 h-4 mr-3" />
                      See Details</button
                    >
                    <button
                      class="btn-dropdown"
                      role="menuitem"
                      onclick={async () => {
                        selected = true
                        await wait(40)
                        await deleteTask(task)
                        selected = false
                        openTaskDropdown = false
                      }}
                    >
                      <Trash2 class="text-gray-400 w-4 h-4 mr-3" />
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            </Dropdown>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>

<!--Task Detail-->
<Modal bind:open={openTaskDetail} large={true}>
  <TaskDetail taskId={task.id} />
</Modal>
